import React, { useEffect, useState } from "react";
import {
  Container,
  Row,
  Col,
  Card,
  Button,
  Form,
  Alert,
  Badge,
  Spinner,
  Table,
  Carousel,
  InputGroup,
  Modal,
  OverlayTrigger,
  Tooltip as ReactBootstrapTooltip,
} from "react-bootstrap";
import axios from "axios";
import { useParams, useNavigate } from "react-router-dom";
import {
  Building,
  GeoAlt,
  Calendar,
  Clock,
  Scissors,
  Person,
  CartPlus,
  CartCheck,
  CreditCard,
  XCircleFill,
  CheckCircleFill,
  Heart,
  HeartFill,
} from "react-bootstrap-icons";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import Swal from "sweetalert2";
import API_BASE_URL from "../config";

// Define categories and subcategories requiring date range bookings using IDs
const DATE_RANGE_CATEGORIES = {
  5: [38, 39, 36],
  6: [41, 42],
  10: [72, 67, 68, 69, 71],
  7: [52, 47, 48, 49, 50, 54],
  9: [64],
  12: [78],
  13: [81, 83, 84],
  4: [32, 34],
  1: [1, 8],
};

// Category to icon mapping
const CATEGORY_ICONS = {
  1: "bi-briefcase",
  2: "bi-heart-pulse",
  3: "bi-book",
  4: "bi-house",
  5: "bi-building",
  6: "bi-car-front",
  7: "bi-camera",
  8: "bi-shop",
  9: "bi-paw",
  10: "bi-airplane",
  11: "bi-brush",
  12: "bi-washing-machine",
  13: "bi-person-wheelchair",
};

export default function CompanyLanding() {
  const { companySlug } = useParams();
  const [company, setCompany] = useState(null);
  const [branches, setBranches] = useState([]);
  const [services, setServices] = useState([]);
  const [filteredServices, setFilteredServices] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedBranch, setSelectedBranch] = useState("");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [time, setTime] = useState("");
  const [openDates, setOpenDates] = useState([]);
  const [availableTimes, setAvailableTimes] = useState([]);
  const [validation, setValidation] = useState({ isValid: false, message: "" });
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [requiresDateRange, setRequiresDateRange] = useState(false);
  const [cart, setCart] = useState([]);
  const [wishlist, setWishlist] = useState([]);
  const [serviceImages, setServiceImages] = useState({});
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [showImageModal, setShowImageModal] = useState(false);
  const [selectedServiceImages, setSelectedServiceImages] = useState([]);
  // Add these state variables near the top
  const [taxPercent, setTaxPercent] = useState(0);
  const [cartDiscounts, setCartDiscounts] = useState([]);
  const [userCountry, setUserCountry] = useState("IN"); // Auto-detect
  const [offers, setOffers] = useState([]); // New state for active offers
  const navigate = useNavigate();
  // Accurate country detection using Geolocation + BigDataCloud
  useEffect(() => {
    const detectUserCountry = () => {
      if (navigator.geolocation) {
        navigator.geolocation.getCurrentPosition(
          async (position) => {
            try {
              const res = await fetch(
                `https://api.bigdatacloud.net/data/reverse-geocode-client?latitude=${position.coords.latitude}&longitude=${position.coords.longitude}&localityLanguage=en`
              );
              const data = await res.json();
              const countryCode = data.countryCode || "IN";
              setUserCountry(countryCode.toUpperCase());
            } catch (err) {
              console.warn("Geolocation API failed, falling back to IN");
              setUserCountry("IN");
            }
          },
          (err) => {
            console.warn("User denied geolocation, defaulting to India");
            setUserCountry("IN");
          }
        );
      } else {
        console.warn("Geolocation not supported, defaulting to India");
        setUserCountry("IN");
      }
    };

    detectUserCountry();
  }, []);

  // Check authentication status
  useEffect(() => {
    async function checkAuth() {
      try {
        const response = await axios.get(`${API_BASE_URL}/auth/check`, {
          withCredentials: true,
          headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
        });
        setIsLoggedIn(true);
      } catch (err) {
        setIsLoggedIn(false);
      }
    }
    checkAuth();
  }, []);

  // Load company, branches, services, wishlist, service images, and active offers
  useEffect(() => {
    async function load() {
      setLoading(true);
      try {
        const promises = [
          axios.get(`${API_BASE_URL}/companies/${companySlug}/public`),
        ];
        if (isLoggedIn) {
          promises.push(
            axios
              .get(`${API_BASE_URL}/wishlist`, { withCredentials: true })
              .catch(() => ({ data: { wishlist: [] } }))
          );
        } else {
          promises.push(Promise.resolve({ data: { wishlist: [] } }));
        }

        const [companyRes, wishlistRes] = await Promise.all(promises);

        const companyData = companyRes.data.company;
        setCompany(companyData);

        setTaxPercent(companyRes.data.tax_percent || 0);
        setCartDiscounts(companyRes.data.discounts || []);
        setBranches(companyRes.data.branches || []);
        const servicesData = companyRes.data.services || [];
        const updatedServices = servicesData.map((service) => ({
          ...service,
          image: service.image
            ? `${API_BASE_URL}${service.image.replace(/^\/api/, "")}`
            : null,
        }));
        setServices(updatedServices);
        setFilteredServices(updatedServices);
        setWishlist(wishlistRes.data.wishlist || []);

        // Fetch service images
        const images = {};
        for (const service of servicesData) {
          const res = await axios
            .get(`${API_BASE_URL}/services/${service.id}/images`)
            .catch(() => ({ data: { images: [] } }));
          images[service.id] = res.data.images || [];
        }
        setServiceImages(images);

        // Fetch active offers
        const offersRes = await axios.get(
          `${API_BASE_URL}/companies/${companyData.id}/offers/active`
        );
        setOffers(offersRes.data || []);

        // Determine if date range is required
        const categoryId = companyData?.category_id;
        const subcategoryId = companyData?.subcategory_id;
        const subcategories = DATE_RANGE_CATEGORIES[categoryId] || [];
        setRequiresDateRange(subcategories.includes(subcategoryId));
      } catch (err) {
        console.error("Error loading data:", err);
        setError("Failed to load company details.");
      } finally {
        setLoading(false);
      }
    }
    load();
  }, [companySlug, isLoggedIn]);

  // Client-side filtering
  useEffect(() => {
    const filtered = services.filter((s) =>
      s.name.toLowerCase().includes(searchTerm.toLowerCase())
    );
    setFilteredServices(filtered);
  }, [searchTerm, services]);

  // Fetch open dates for 6 months
  useEffect(() => {
    if (company) {
      async function fetchOpenDates() {
        const now = new Date();
        const startDate = new Date(now.getFullYear(), now.getMonth(), 1);
        const endDate = new Date(now.getFullYear(), now.getMonth() + 6, 1);

        const start = startDate.toISOString().split("T")[0];
        const end = endDate.toISOString().split("T")[0];

        try {
          const res = await axios.get(
            `${API_BASE_URL}/companies/${company.id}/open-dates`,
            {
              params: {
                start,
                end,
                branch_id: selectedBranch || null,
              },
            }
          );
          setOpenDates(res.data.open_dates || []);
        } catch (err) {
          console.error("Open dates error:", err.response?.data || err.message);
          setOpenDates([]);
        }
      }
      fetchOpenDates();
    }
  }, [company, selectedBranch]);

  // Validate booking
  useEffect(() => {
    if (!fromDate || (requiresDateRange && !toDate)) {
      setValidation({
        isValid: false,
        message: requiresDateRange ? "Select from and to dates" : "Select date",
      });
      setAvailableTimes([]);
      return;
    }

    if (requiresDateRange && toDate && new Date(toDate) < new Date(fromDate)) {
      setValidation({
        isValid: false,
        message: "To date must be after from date",
      });
      setAvailableTimes([]);
      return;
    }

    async function validateBooking() {
      try {
        const params = {
          date: fromDate,
          to_date: requiresDateRange ? toDate : null,
          branch_id: selectedBranch || null,
        };
        const res = await axios.get(
          `${API_BASE_URL}/companies/${company.id}/validate-booking`,
          { params }
        );

        setAvailableTimes(res.data.available_times || []);
        setValidation({
          isValid:
            res.data.is_valid &&
            (!requiresDateRange || (requiresDateRange && toDate)),
          message: res.data.message || "Booking available",
        });
      } catch (err) {
        console.error("Validation error:", err.response?.data || err.message);
        setValidation({ isValid: false, message: "Validation error" });
      }
    }

    validateBooking();
  }, [fromDate, toDate, selectedBranch, company, requiresDateRange]);

  // Handle employee selection
  const handleEmployeeSelect = (serviceId, empId) => {
    setServices((prev) =>
      prev.map((s) =>
        s.id === serviceId ? { ...s, selectedEmployeeId: empId } : s
      )
    );
  };

  // Wishlist handling
  const toggleWishlist = async (serviceId) => {
    if (!isLoggedIn) {
      showError("Login Required", "Please log in to manage your wishlist.");
      return;
    }

    try {
      const isInWishlist = wishlist.some((w) => w.service_id === serviceId);
      if (isInWishlist) {
        await axios.delete(`${API_BASE_URL}/wishlist/remove`, {
          data: { service_id: serviceId },
          withCredentials: true,
        });
        setWishlist((prev) => prev.filter((w) => w.service_id !== serviceId));
        showSuccess(
          "Removed from Wishlist",
          "Service removed from your wishlist."
        );
        return;
      }

      const res = await axios.post(
        `${API_BASE_URL}/wishlist/add`,
        { company_id: company.id, service_id: serviceId },
        { withCredentials: true }
      );
      setWishlist((prev) => [
        ...prev,
        {
          service_id: serviceId,
          company_id: company.id,
          service_name: services.find((s) => s.id === serviceId)?.name,
          company_name: company.name,
          date_added: new Date().toISOString(),
        },
      ]);
      showSuccess("Added to Wishlist", res.data.message);
    } catch (err) {
      showError(
        "Wishlist Error",
        err.response?.data?.message || "Failed to update wishlist"
      );
    }
  };

  // Handle carousel click to open modal
  const handleCarouselClick = (serviceId) => {
    setSelectedServiceImages(serviceImages[serviceId] || []);
    setShowImageModal(true);
  };

  // SweetAlert messages
  const showSuccess = (title, message) => {
    Swal.fire({
      icon: "success",
      title,
      text: message,
      timer: 2000,
      showConfirmButton: false,
      toast: true,
      position: "top-end",
    });
  };

  const showError = (title, message) => {
    Swal.fire({
      icon: "error",
      title,
      text: message,
      timer: 3000,
      showConfirmButton: false,
      toast: true,
      position: "top-end",
    });
  };

  // Calculate price for date range
  const calculatePrice = (service, from, to) => {
    if (!requiresDateRange || !from || !to) return service.price_cents;
    const fromDate = new Date(from);
    const toDate = new Date(to);
    const days = Math.ceil((toDate - fromDate) / (1000 * 60 * 60 * 24)) + 1;
    return service.price_cents * days;
  };
  // NEW: Final cart pricing with tax + discount
  const getCartPricing = () => {
    const baseTotal = cart.reduce((sum, item) => sum + item.price_cents, 0);

    let discountINR = 0;
    for (const d of cartDiscounts) {
      if (baseTotal * 100 >= d.threshold_cents) {
        if (d.discount_percent > 0)
          discountINR = baseTotal * (d.discount_percent / 100);
        else if (d.discount_flat_cents > 0)
          discountINR = d.discount_flat_cents / 100;
        break;
      }
    }

    const afterDiscount = baseTotal - discountINR;
    const taxINR = afterDiscount * (taxPercent / 100);
    const finalTotal = afterDiscount + taxINR;

    return {
      base: Number(baseTotal.toFixed(2)),
      discount: Number(discountINR.toFixed(2)),
      tax: Number(taxINR.toFixed(2)),
      total: Number(finalTotal.toFixed(2)),
    };
  };

  const pricing = getCartPricing();
  const addToCart = (service) => {
    if (!validation.isValid || (requiresDateRange && !toDate)) {
      showError(
        "Missing Selection",
        validation.message || "Please select valid dates"
      );
      return;
    }
    if (!selectedBranch && branches.length > 0) {
      showError("Branch Required", "Please select a branch");
      return;
    }
    if (!requiresDateRange && !time) {
      showError("Time Required", "Please select a time for this service");
      return;
    }

    const start = time ? new Date(`${fromDate}T${time}`) : new Date(fromDate);
    const selectedEmployeeId = service.selectedEmployeeId || null;
    const price = calculatePrice(service, fromDate, toDate);

    setCart((prevCart) => [
      ...prevCart,
      {
        service_id: service.id,
        service_name: service.name,
        employee_id: selectedEmployeeId,
        employee_name:
          service.employees?.find((e) => e.id === selectedEmployeeId)?.name ||
          "Any",
        branch_id: selectedBranch || null,
        from_date: fromDate,
        to_date: requiresDateRange ? toDate : null,
        scheduled_at: time ? start.toISOString() : null,
        scheduled_start: new Date(fromDate).toISOString(),
        scheduled_end: requiresDateRange
          ? new Date(toDate).toISOString()
          : null,
        price_cents: price,
      },
    ]);

    showSuccess(
      `${service.name} Added!`,
      `${
        requiresDateRange
          ? `${fromDate} to ${toDate}`
          : `${fromDate} at ${time}`
      } • ${service.name} • ₹${price.toFixed(2)}`
    );
  };

  const removeFromCart = (index) => {
    const item = cart[index];
    setCart((prevCart) => prevCart.filter((_, i) => i !== index));
    showSuccess("Removed!", `${item.service_name}`);
  };

  // Replace the existing goToCheckout
  const goToCheckout = () => {
    if (cart.length === 0) return;
    const gateway = userCountry === "IN" ? "razorpay" : "stripe";
    // Store cart + company info (including accept_online_payments)
    localStorage.setItem(
      "checkoutData",
      JSON.stringify({
        cart,
        company: {
          id: company.id,
          slug: company.slug,
          name: company.name,
          accept_online_payments: company.accept_online_payments,
        },
        pricing_breakdown: pricing,
        tax_percent: taxPercent,
        applied_discount: pricing.discount,
        gateway,
        user_country: userCountry,
      })
    );
    navigate(`/company/${companySlug}/checkout`);
  };

  const getMinDate = () => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return today;
  };

  if (error) {
    return (
      <Container className="py-4">
        <Row className="justify-content-center">
          <Col md={8}>
            <Alert variant="danger">
              <Building className="me-2" />
              {error}
            </Alert>
          </Col>
        </Row>
      </Container>
    );
  }

  if (loading || !company) {
    return (
      <Container className="py-5 text-center">
        <Spinner animation="border" size="sm" className="me-2" />
        <div>Loading company details...</div>
      </Container>
    );
  }

  return (
    <>
      <style>
        {`
          .react-datepicker-popper {
            z-index: 1000 !important;
          }
          .service-card {
            background-size: cover;
            background-position: center;
            position: relative;
            overflow: hidden;
          }
          .service-card::before {
            content: '';
            position: absolute;
            top: 0;
            left: 0;
            right: 0;
            bottom: 0;
            background: rgba(255, 255, 255, 0.8);
            z-index: 1;
          }
          .service-card > * {
            position: relative;
            z-index: 2;
          }
          .wishlist-heart {
            position: absolute;
            top: 10px;
            right: 10px;
            cursor: pointer;
            z-index: 3;
          }
          .service-image {
            max-height: 150px;
            object-fit: cover;
          }
          .modal-image {
            max-height: 80vh;
            object-fit: contain;
            width: 100%;
          }
          .modal-carousel {
            max-width: 90vw;
            margin: auto;
          }
        `}
      </style>
      <Container className="py-3 px-3 px-md-4">
        {/* Company Header */}
        <Row className="justify-content-center mb-4">
          <Col xs={12} md={10}>
            <Card className="border-0 shadow-sm bg-primary bg-opacity-10">
              <Card.Body className="text-center">
                {company.logo && (
                  <img
                    src={company.logo}
                    alt={`${company.name} logo`}
                    style={{ maxWidth: "100px", marginBottom: "10px" }}
                  />
                )}
                <Building className="text-primary mb-2" size={48} />
                <Card.Title
                  as="h2"
                  className="mb-1 d-flex align-items-center justify-content-center"
                >
                  {company.name}
                  {company.is_verified === 1 && (
                    <OverlayTrigger
                      placement="top"
                      overlay={
                        <ReactBootstrapTooltip>
                          This company has been verified by TickTickGo
                        </ReactBootstrapTooltip>
                      }
                    >
                      <span
                        className="badge bg-success text-white ms-2"
                        style={{ fontSize: "0.8rem", padding: "0.35em 0.65em" }}
                      >
                        <i className="bi bi-check-circle-fill me-1"></i>
                        TickTickGo Verified
                      </span>
                    </OverlayTrigger>
                  )}
                </Card.Title>
                <div className="d-flex align-items-center justify-content-center mt-2">
                  {company.avg_rating > 0 ? (
                    <>
                      <div className="text-warning me-2">
                        {Array.from({ length: 5 }).map((_, i) => (
                          <i
                            key={i}
                            title={`${i + 1} Star`}
                            className={`bi ${
                              i < Math.round(company.avg_rating)
                                ? "bi-star-fill"
                                : "bi-star"
                            } me-1 text-warning`}
                          />
                        ))}
                      </div>
                      <small className="text-muted">
                        {company.avg_rating.toFixed(1)} ({company.total_reviews}
                        )
                      </small>
                    </>
                  ) : (
                    <small className="text-muted">No reviews yet</small>
                  )}
                </div>
                <Card.Text className="mb-0">
                  <GeoAlt className="me-1" />
                  {company.owner_name} • {company.city}, {company.state}
                </Card.Text>
              </Card.Body>
            </Card>
          </Col>
        </Row>

        {/* Current Offers Section */}
        {offers.length > 0 && (
          <Row className="mb-4">
            <Col xs={12}>
              <div className="d-flex align-items-center mb-3">
                <i
                  className="bi bi-gift me-2 text-danger"
                  style={{ fontSize: "1.5rem" }}
                />
                <h4 className="mb-0">Current Offers</h4>
              </div>
              <Carousel>
                {offers.map((offer) => (
                  <Carousel.Item key={offer.id}>
                    {offer.image_url && (
                      <img
                        className="d-block w-100"
                        src={offer.image_url}
                        alt="Offer image"
                        style={{ maxHeight: "300px", objectFit: "cover" }}
                      />
                    )}
                    <Carousel.Caption
                      style={{
                        backgroundColor: "rgba(0,0,0,0.5)",
                        padding: "10px",
                      }}
                    >
                      <h5>{offer.description}</h5>
                      <p>
                        Valid: {offer.start_date} to {offer.end_date}
                        {offer.branch_name !== "All" &&
                          ` • Branch: ${offer.branch_name}`}
                      </p>
                      {offer.youtube_video && (
                        <a
                          href={offer.youtube_video}
                          target="_blank"
                          rel="noreferrer"
                          className="btn btn-sm btn-light"
                        >
                          Watch Video
                        </a>
                      )}
                    </Carousel.Caption>
                  </Carousel.Item>
                ))}
              </Carousel>
            </Col>
          </Row>
        )}

        {/* Search and Filter */}
        <Row className="mb-4">
          <Col xs={12} md={6}>
            <InputGroup>
              <InputGroup.Text>
                <i className="bi bi-search" />
              </InputGroup.Text>
              <Form.Control
                placeholder="Search services..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </InputGroup>
          </Col>
        </Row>

        {/* Date/Time & Branch */}
        <Row className="g-3 mb-4">
          {branches.length > 0 && (
            <Col xs={12} md={4}>
              <Card className="border-0 shadow-sm h-100">
                <Card.Body>
                  <div className="d-flex align-items-center mb-2">
                    <GeoAlt className="me-2 text-info" size={16} />
                    <strong>Select Branch</strong>
                  </div>
                  <Form.Select
                    value={selectedBranch}
                    onChange={(e) => {
                      setSelectedBranch(e.target.value);
                      setFromDate("");
                      setToDate("");
                      setTime("");
                    }}
                    size="sm"
                  >
                    <option value="">-- All Branches --</option>
                    {branches.map((b) => (
                      <option key={b.id} value={b.id}>
                        {b.name} ({b.city})
                      </option>
                    ))}
                  </Form.Select>
                </Card.Body>
              </Card>
            </Col>
          )}
          <Col xs={12} md={branches.length > 0 ? 8 : 12}>
            <Card className="border-0 shadow-sm h-100">
              <Card.Body>
                <div className="d-flex align-items-center mb-2">
                  <Calendar className="me-2 text-warning" size={16} />
                  <strong>Appointment</strong>
                </div>
                <div className="d-flex gap-2 mb-2 flex-wrap">
                  <DatePicker
                    selected={fromDate ? new Date(fromDate) : null}
                    onChange={(d) => {
                      const newDate = d ? d.toISOString().split("T")[0] : "";
                      setFromDate(newDate);
                      setTime("");
                      if (
                        newDate &&
                        toDate &&
                        new Date(toDate) < new Date(newDate)
                      ) {
                        setToDate("");
                      }
                    }}
                    minDate={getMinDate()}
                    dateFormat="yyyy-MM-dd"
                    className="form-control form-control-sm"
                    placeholderText="From Date"
                    excludeDates={
                      openDates.length > 0
                        ? Array.from({ length: 365 }, (_, i) => {
                            const d = new Date();
                            d.setDate(d.getDate() + i);
                            const dStr = d.toISOString().split("T")[0];
                            return openDates.includes(dStr) ? null : d;
                          }).filter(Boolean)
                        : []
                    }
                    popperPlacement="bottom-start"
                  />
                  {requiresDateRange && (
                    <DatePicker
                      selected={toDate ? new Date(toDate) : null}
                      onChange={(d) => {
                        const newDate = d ? d.toISOString().split("T")[0] : "";
                        setToDate(newDate);
                        setTime("");
                      }}
                      minDate={fromDate ? new Date(fromDate) : getMinDate()}
                      dateFormat="yyyy-MM-dd"
                      className="form-control form-control-sm"
                      placeholderText="To Date"
                      excludeDates={
                        openDates.length > 0
                          ? Array.from({ length: 365 }, (_, i) => {
                              const d = new Date();
                              d.setDate(d.getDate() + i);
                              const dStr = d.toISOString().split("T")[0];
                              return openDates.includes(dStr) ? null : d;
                            }).filter(Boolean)
                          : []
                      }
                      popperPlacement="bottom-start"
                    />
                  )}
                  {!requiresDateRange && (
                    <Form.Select
                      value={time}
                      onChange={(e) => setTime(e.target.value)}
                      size="sm"
                      className="flex-fill"
                      disabled={!fromDate || availableTimes.length === 0}
                    >
                      <option value="">Select Time</option>
                      {availableTimes.map((t) => (
                        <option key={t} value={t}>
                          {t}
                        </option>
                      ))}
                    </Form.Select>
                  )}
                </div>
                <Badge
                  bg={
                    validation.isValid
                      ? "success"
                      : fromDate || toDate
                      ? "danger"
                      : "secondary"
                  }
                  className="d-flex align-items-center gap-1"
                >
                  {validation.isValid ? (
                    <>
                      <CheckCircleFill size={12} />
                      {validation.message}
                    </>
                  ) : fromDate || toDate ? (
                    <>
                      <XCircleFill size={12} />
                      {validation.message}
                    </>
                  ) : requiresDateRange ? (
                    "Select from and to dates"
                  ) : (
                    "Select date"
                  )}
                </Badge>
                {availableTimes.length === 0 &&
                  fromDate &&
                  !requiresDateRange && (
                    <small className="text-muted d-block mt-1">
                      No times available
                    </small>
                  )}
              </Card.Body>
            </Card>
          </Col>
        </Row>

        {/* Services Grid */}
        <Row className="mb-4">
          <Col xs={12}>
            <div className="d-flex align-items-center mb-3">
              <Scissors className="me-2 text-success" size={24} />
              <h4 className="mb-0">Available Services</h4>
            </div>
          </Col>
          {filteredServices.map((s) => (
            <Col xs={12} sm={6} md={4} lg={3} key={s.id} className="mb-3">
              <Card
                className="h-100 shadow-sm border-0 service-card"
                style={{
                  backgroundImage: s.image ? `url(${s.image})` : "none",
                }}
              >
                <Card.Body className="d-flex flex-column">
                  {isLoggedIn && (
                    <div
                      className="wishlist-heart"
                      onClick={() => toggleWishlist(s.id)}
                    >
                      {wishlist.some((w) => w.service_id === s.id) ? (
                        <HeartFill color="red" size={20} />
                      ) : (
                        <Heart size={20} />
                      )}
                    </div>
                  )}
                  <i
                    className={`bi ${
                      CATEGORY_ICONS[s.category_id] || "bi-scissors"
                    } text-primary mb-2`}
                    style={{ fontSize: "1.5rem" }}
                  />
                  <Card.Title className="fs-5 fw-bold mb-2">
                    {s.name}
                  </Card.Title>
                  <div className="text-muted small mb-2">
                    <Clock className="me-1" size={12} />
                    {s.duration_minutes} min
                  </div>
                  <div className="h5 text-primary mb-3 fw-bold">
                    ₹{calculatePrice(s, fromDate, toDate).toFixed(2)}
                    <small className="text-muted fs-6">
                      {" "}
                      (Excl. taxes & fees)
                    </small>
                  </div>

                  {s.employees && s.employees.length > 0 ? (
                    <>
                      <div className="small text-muted mb-2">Employee</div>
                      <Form.Select
                        size="sm"
                        value={s.selectedEmployeeId || ""}
                        onChange={(e) =>
                          handleEmployeeSelect(s.id, e.target.value)
                        }
                        className="mb-3"
                      >
                        <option value="">-- Any --</option>
                        {s.employees.map((emp) => (
                          <option key={emp.id} value={emp.id}>
                            {emp.name}
                          </option>
                        ))}
                      </Form.Select>
                    </>
                  ) : (
                    <div className="small text-muted mb-3">
                      <Person className="me-1" size={12} />
                      Any employee
                    </div>
                  )}

                  <Button
                    variant="primary"
                    size="sm"
                    onClick={() => addToCart(s)}
                    disabled={
                      !validation.isValid || (!requiresDateRange && !time)
                    }
                    className="mt-auto d-flex align-items-center gap-1"
                  >
                    <CartPlus size={16} />
                    {validation.isValid && (requiresDateRange || time)
                      ? "Add to Cart"
                      : requiresDateRange
                      ? "Select Dates"
                      : "Select Date/Time"}
                  </Button>

                  {serviceImages[s.id]?.length > 0 && (
                    <Carousel
                      className="mt-3"
                      onClick={() => handleCarouselClick(s.id)}
                      style={{ cursor: "pointer" }}
                    >
                      {serviceImages[s.id].map((img, idx) => (
                        <Carousel.Item key={idx}>
                          <img
                            className="d-block w-100 service-image"
                            src={img.image_url}
                            alt={`Service image ${idx + 1}`}
                            // onError={(e) => {
                            //   e.target.src = "/fallback-image.png";
                            // }}
                          />
                          <Carousel.Caption>
                            <p className="p small text-dark bg-light bg-opacity-75 p-1">
                              {img.description || `Image ${idx + 1}`}
                            </p>
                          </Carousel.Caption>
                        </Carousel.Item>
                      ))}
                    </Carousel>
                  )}
                </Card.Body>
              </Card>
            </Col>
          ))}
        </Row>

        {/* UPDATED CART SUMMARY */}
        {cart.length > 0 && (
          <Row className="mb-4">
            <Col xs={12}>
              <Card className="shadow-sm border-0">
                <Card.Body>
                  <div className="d-flex align-items-center justify-content-between mb-3">
                    <div className="d-flex align-items-center">
                      <CartCheck className="me-2 text-success" size={24} />
                      <h5 className="mb-0">Your Cart ({cart.length})</h5>
                    </div>
                    <div className="text-end">
                      <Badge bg="success" className="fs-5">
                        Final: ₹{pricing.total.toFixed(2)}
                      </Badge>
                      {pricing.discount > 0 && (
                        <small className="d-block text-success">
                          Saved ₹{pricing.discount.toFixed(2)}
                        </small>
                      )}
                    </div>
                  </div>

                  <Table size="sm" className="mb-3">
                    <tbody>
                      <tr>
                        <td>Service Total</td>
                        <td className="text-end">₹{pricing.base.toFixed(2)}</td>
                      </tr>
                      {pricing.discount > 0 && (
                        <tr className="text-success">
                          <td>Discount</td>
                          <td className="text-end">
                            -₹{pricing.discount.toFixed(2)}
                          </td>
                        </tr>
                      )}
                      <tr>
                        <td>Tax ({taxPercent}%)</td>
                        <td className="text-end">₹{pricing.tax.toFixed(2)}</td>
                      </tr>
                      <tr className="table-success fw-bold">
                        <td>Amount Payable</td>
                        <td className="text-end">
                          ₹{pricing.total.toFixed(2)}
                        </td>
                      </tr>
                    </tbody>
                  </Table>

                  <Table size="sm">
                    <tbody>
                      {cart.map((c, i) => (
                        <tr key={i}>
                          <td>
                            {c.service_name} • ₹{c.price_cents.toFixed(2)}
                          </td>
                          <td className="text-end">
                            <Button
                              variant="link"
                              size="sm"
                              className="text-danger p-0"
                              onClick={() => removeFromCart(i)}
                            >
                              Remove
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>

                  <div className="d-grid mt-3">
                    <Button variant="success" size="lg" onClick={goToCheckout}>
                      <CreditCard className="me-2" /> Proceed to Checkout → ₹
                      {pricing.total.toFixed(2)}
                    </Button>
                  </div>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        )}

        {/* Full-Screen Image Modal */}
        <Modal
          show={showImageModal}
          onHide={() => setShowImageModal(false)}
          fullscreen
          centered
        >
          <Modal.Header closeButton>
            <Modal.Title>Service Images</Modal.Title>
          </Modal.Header>
          <Modal.Body className="p-0">
            <Carousel className="modal-carousel">
              {selectedServiceImages.map((img, idx) => (
                <Carousel.Item key={idx}>
                  <img
                    className="modal-image"
                    src={img.image_url}
                    alt={`Service image ${idx + 1}`}
                    // onError={(e) => {
                    //   e.target.src = "/fallback-image.png";
                    // }}
                  />
                  <Carousel.Caption>
                    <p className="text-dark bg-light bg-opacity-75 p-2">
                      {img.description || `Image ${idx + 1}`}
                    </p>
                  </Carousel.Caption>
                </Carousel.Item>
              ))}
            </Carousel>
          </Modal.Body>
        </Modal>
      </Container>
    </>
  );
}
